import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useAuthStore } from '../stores/authStore'
import { useToast } from '../hooks/useToast'

interface User {
  id: string
  firstName: string
  lastName: string
  email: string
  isActive: boolean
  createdAt: string
  staffOrganizationId?: string
  roles: Array<{
    id: string
    role: {
      id: string
      name: string
      description: string
    }
  }>
  clientOrganizations: Array<{
    id: string
    name: string
  }>
  staffOrganization?: {
    id: string
    name: string
  }
}

interface Role {
  id: string
  name: string
  description: string
}

interface Organization {
  id: string
  name: string
  type: 'client' | 'staff'
}

const UserManagement: React.FC = () => {
  const { user } = useAuthStore()
  const { showError, showSuccess } = useToast()
  const [users, setUsers] = useState<User[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAssignClientModal, setShowAssignClientModal] = useState(false)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showDeletedUsers, setShowDeletedUsers] = useState(false)
  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  })
  const [isResettingPassword, setIsResettingPassword] = useState(false)

  // Helper function to determine if selected roles are staff-related
  const isStaffRole = (roleIds: string[]) => {
    const staffRoleNames = ['administrator', 'support_staff', 'manager', 'staff']
    console.log('🔍 isStaffRole called with:', roleIds)
    console.log('🔍 Available roles:', roles)
    console.log('🔍 Staff role names:', staffRoleNames)
    
    const result = roleIds.some(roleId => {
      const role = roles.find(r => r.id === roleId)
      console.log('🔍 Checking roleId:', roleId, 'Found role:', role)
      return role && staffRoleNames.includes(role.name)
    })
    
    console.log('🔍 isStaffRole result:', result)
    return result
  }

  // Helper function to determine if selected roles are client-related
  const isClientRole = (roleIds: string[]) => {
    const clientRoleNames = ['client', 'ticket_manager']
    console.log('🔍 isClientRole called with:', roleIds)
    console.log('🔍 Available roles:', roles)
    console.log('🔍 Client role names:', clientRoleNames)
    
    const result = roleIds.some(roleId => {
      const role = roles.find(r => r.id === roleId)
      console.log('🔍 Checking roleId:', roleId, 'Found role:', role)
      return role && clientRoleNames.includes(role.name)
    })
    
    console.log('🔍 isClientRole result:', result)
    return result
  }

  // New state for dynamic organization selection
  const [selectedRoleType, setSelectedRoleType] = useState<'staff' | 'client' | null>(null)
  const [createForm, setCreateForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    roleIds: [] as string[],
    staffOrganizationId: '',
    clientOrganizationId: '',
    isActive: true
  })
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    roleIds: [] as string[],
    staffOrganizationId: '',
    clientOrganizationId: '',
    isActive: true
  })

  useEffect(() => {
    fetchUsers()
    fetchRoles()
    fetchOrganizations()
  }, [])

  // Fetch users whenever the toggle state changes
  useEffect(() => {
    fetchUsers()
  }, [showDeletedUsers])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/users', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      // Apply filtering based on toggle state
      const allUsers = response.data.data || []
      if (showDeletedUsers) {
        // Show all users including deleted ones
        setUsers(allUsers)
      } else {
        // Filter out deleted users (those with isActive: false)
        const activeUsers = allUsers.filter((user: User) => user.isActive)
        setUsers(activeUsers)
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      showError('Failed to load users')
      setUsers([]) // Ensure users is always an array
    } finally {
      setLoading(false)
    }
  }

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/api/roles', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      setRoles(response.data.data || [])
    } catch (error) {
      console.error('Error fetching roles:', error)
      setRoles([]) // Ensure roles is always an array
    }
  }

  const fetchOrganizations = async () => {
    try {
      // Fetch both client and staff organizations
      const [clientResponse, staffResponse] = await Promise.all([
        axios.get('/api/client-organizations', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get('/api/staff-organizations', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ])

      const clientOrgs = clientResponse.data.data.map((org: any) => ({
        ...org,
        type: 'client' as const
      }))
      const staffOrgs = staffResponse.data.data.map((org: any) => ({
        ...org,
        type: 'staff' as const
      }))

      setOrganizations([...clientOrgs, ...staffOrgs])
    } catch (error) {
      console.error('Error fetching organizations:', error)
      setOrganizations([]) // Ensure organizations is always an array
    }
  }

  const handleCreateUser = async () => {
    try {
      if (!createForm.firstName || !createForm.lastName || !createForm.email || !createForm.password) {
        showError('Please fill in all required fields')
        return
      }

      if (createForm.roleIds.length === 0) {
        showError('Please select at least one role')
        return
      }

      // Process organization data based on role type
      const createData = { ...createForm }
      console.log('🔍 Original createForm:', createForm)
      console.log('🔍 Processing createData:', createData)
      
      // Determine role type for this user
      let userRoleType: 'staff' | 'client' | null = null
      if (isStaffRole(createForm.roleIds)) {
        userRoleType = 'staff'
      } else if (isClientRole(createForm.roleIds)) {
        userRoleType = 'client'
      }
      
      console.log('🔍 User role type:', userRoleType)
      
      // Handle organization data based on role type
      if (userRoleType === 'staff') {
        // Staff users need staffOrganizationId
        if (!createData.staffOrganizationId || createData.staffOrganizationId.trim() === '') {
          console.log('🔍 Staff user: Removing staffOrganizationId - empty or whitespace')
          delete createData.staffOrganizationId
        } else {
          console.log('🔍 Staff user: Keeping staffOrganizationId:', createData.staffOrganizationId)
        }
        // Remove client organization data for staff users
        delete createData.clientOrganizationId
      } else if (userRoleType === 'client') {
        // Client users don't need staffOrganizationId
        console.log('🔍 Client user: Removing staffOrganizationId')
        delete createData.staffOrganizationId
        // Keep clientOrganizationId for client users
        if (!createData.clientOrganizationId || createData.clientOrganizationId.trim() === '') {
          console.log('🔍 Client user: Removing clientOrganizationId - empty or whitespace')
          delete createData.clientOrganizationId
        } else {
          console.log('🔍 Client user: Keeping clientOrganizationId:', createData.clientOrganizationId)
        }
      } else {
        // No clear role type, remove both
        console.log('🔍 No clear role type: Removing both organization fields')
        delete createData.staffOrganizationId
        delete createData.clientOrganizationId
      }
      
      console.log('🔍 Final createData to send:', createData)

      const response = await axios.post('/api/users', createData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.data.success) {
        showSuccess('User created successfully')
        setCreateForm({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          roleIds: [],
          staffOrganizationId: '',
          clientOrganizationId: '',
          isActive: true
        })
        setShowCreateModal(false)
        setSelectedRoleType(null)
        fetchUsers()
      }
    } catch (error) {
      console.error('Error creating user:', error)
      showError('Failed to create user')
    }
  }

  const handleEditUser = async () => {
    try {
      if (!selectedUser) return

      if (!editForm.firstName || !editForm.lastName || !editForm.email) {
        showError('Please fill in all required fields')
        return
      }

      if (editForm.roleIds.length === 0) {
        showError('Please select at least one role')
        return
      }

      // Only send organization IDs if they have actually changed
      const updateData = { ...editForm }
      if (updateData.staffOrganizationId === selectedUser.staffOrganizationId) {
        delete updateData.staffOrganizationId
      }
      if (updateData.clientOrganizationId === (selectedUser.clientOrganizations?.[0]?.id || '')) {
        delete updateData.clientOrganizationId
      }

      const response = await axios.put(`/api/users/${selectedUser.id}`, updateData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.data.success) {
        showSuccess('User updated successfully')
        setShowEditModal(false)
        setSelectedUser(null)
        fetchUsers()
      }
    } catch (error) {
      console.error('Error updating user:', error)
      showError('Failed to update user')
    }
  }

  const handleStatusToggle = async (userId: string, currentStatus: boolean) => {
    try {
      const response = await axios.patch(`/api/users/${userId}/status`, {
        isActive: !currentStatus
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.data.success) {
        showSuccess(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`)
        fetchUsers()
      }
    } catch (error) {
      console.error('Error toggling user status:', error)
      showError('Failed to update user status')
    }
  }

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return
    }

    try {
      const response = await axios.delete(`/api/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.data.success) {
        showSuccess('User deleted successfully')
        fetchUsers()
      }
    } catch (error: any) {
      console.error('Error deleting user:', error)
      const errorMessage = error.response?.data?.error || 'Failed to delete user'
      showError(errorMessage)
    }
  }

  const handleToggleDeletedUsers = () => {
    setShowDeletedUsers(!showDeletedUsers)
  }

  const openEditModal = (user: User) => {
    setSelectedUser(user)
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      roleIds: user.roles.map(r => r.role.id),
      staffOrganizationId: user.staffOrganizationId || '',
      clientOrganizationId: user.clientOrganizations?.[0]?.id || '',
      isActive: user.isActive
    })
    setShowEditModal(true)
  }

  const openAssignClientModal = (user: User) => {
    setSelectedUser(user)
    setShowAssignClientModal(true)
  }

  const handleAssignClient = async (clientOrgId: string) => {
    try {
      if (!selectedUser) return

      // Find the client role ID
      const clientRole = roles.find(role => role.name === 'client')
      if (!clientRole) {
        showError('Client role not found')
        return
      }

      const response = await axios.post(`/api/users/${selectedUser.id}/assign-organization`, {
        organizationId: clientOrgId,
        organizationType: 'client',
        roleId: clientRole.id
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.data.success) {
        showSuccess('User assigned to client organization successfully')
        setShowAssignClientModal(false)
        setSelectedUser(null)
        fetchUsers()
      }
    } catch (error: any) {
      console.error('Error assigning user to client organization:', error)
      showError(error.response?.data?.error || 'Failed to assign user to client organization')
    }
  }

  const openResetPasswordModal = (user: User) => {
    setSelectedUser(user)
    setResetPasswordForm({ newPassword: '', confirmPassword: '' })
    setShowResetPasswordModal(true)
  }

  const handleResetPassword = async () => {
    if (!selectedUser) return

    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      showError('Passwords do not match')
      return
    }

    if (resetPasswordForm.newPassword.length < 6) {
      showError('Password must be at least 6 characters long')
      return
    }

    setIsResettingPassword(true)

    try {
      const response = await axios.post(`/api/users/${selectedUser.id}/reset-password`, {
        newPassword: resetPasswordForm.newPassword
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.data.success) {
        showSuccess('Password reset successfully')
        setShowResetPasswordModal(false)
        setSelectedUser(null)
        setResetPasswordForm({ newPassword: '', confirmPassword: '' })
      } else {
        showError(response.data.error || 'Failed to reset password')
      }
    } catch (error: any) {
      console.error('Error resetting password:', error)
      showError(error.response?.data?.error || 'Failed to reset password')
    } finally {
      setIsResettingPassword(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="mt-2 text-gray-600">
            Manage all users on the platform, their roles, and organization assignments
          </p>
        </div>

        {/* Actions */}
        <div className="mb-6 flex justify-between items-center">
          <div className="flex space-x-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              Add New User
            </button>
          </div>
          <div className="flex items-center space-x-4">
            {/* Toggle for showing deleted users */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="showDeletedUsers"
                checked={showDeletedUsers}
                onChange={handleToggleDeletedUsers}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="showDeletedUsers" className="ml-2 text-sm text-gray-700">
                Show Deleted Users
              </label>
            </div>
            <div className="text-sm text-gray-500">
              {users.length} user{users.length !== 1 ? 's' : ''} total
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Roles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organizations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className={`text-sm font-medium ${
                          !user.isActive 
                            ? 'text-gray-500 line-through' 
                            : 'text-gray-900'
                        }`}>
                          {!user.isActive ? (
                            <>
                              <span className="line-through">{user.firstName} {user.lastName}</span>
                              <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                                DELETED
                              </span>
                            </>
                          ) : (
                            `${user.firstName} ${user.lastName}`
                          )}
                        </div>
                        <div className={`text-sm ${
                          !user.isActive 
                            ? 'text-gray-400' 
                            : 'text-gray-500'
                        }`}>
                          {user.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                                                 {user.roles.map((userRole) => (
                           <span
                             key={userRole.id}
                             className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                           >
                             {userRole.role.name}
                           </span>
                         ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.clientOrganizations.length > 0 && (
                          <div className="mb-1">
                            <span className="font-medium">Client:</span> {user.clientOrganizations.map(org => org.name).join(', ')}
                          </div>
                        )}
                        {user.staffOrganization && (
                          <div>
                            <span className="font-medium">Staff:</span> {user.staffOrganization.name}
                          </div>
                        )}
                        {user.clientOrganizations.length === 0 && !user.staffOrganization && (
                          <span className="text-gray-500">No organizations</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {user.isActive ? (
                          <>
                            <button
                              onClick={() => openEditModal(user)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => openAssignClientModal(user)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Assign Client
                            </button>
                            <button
                              onClick={() => openResetPasswordModal(user)}
                              className="text-purple-600 hover:text-purple-900"
                            >
                              Reset Password
                            </button>
                            <button
                              onClick={() => handleStatusToggle(user.id, user.isActive)}
                              className={`${
                                user.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                              }`}
                            >
                              {user.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <span className="text-gray-400 text-xs">No actions available</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Create New User</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={createForm.firstName}
                    onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={createForm.lastName}
                    onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  id="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                                  <label htmlFor="roles" className="block text-sm font-medium text-gray-700 mb-2">
                    Roles *
                  </label>
                  <select
                    multiple
                    id="roles"
                    value={createForm.roleIds}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value)
                      setCreateForm({ ...createForm, roleIds: selectedOptions })
                      
                      console.log('🔍 Role selection changed:', selectedOptions)
                      console.log('🔍 Available roles:', roles)
                      
                      // Determine role type and update organization fields
                      let newRoleType: 'staff' | 'client' | null = null
                      
                      if (isStaffRole(selectedOptions)) {
                        console.log('🔍 Detected STAFF role')
                        newRoleType = 'staff'
                      } else if (isClientRole(selectedOptions)) {
                        console.log('🔍 Detected CLIENT role')
                        newRoleType = 'client'
                      } else {
                        console.log('🔍 No clear role type detected')
                        newRoleType = null
                      }
                      
                      // Update both state variables
                      setSelectedRoleType(newRoleType)
                      // Clear both organization fields when role changes
                      setCreateForm(prev => ({ 
                        ...prev, 
                        staffOrganizationId: '',
                        clientOrganizationId: ''
                      }))
                      
                      console.log('🔍 Role type set to:', newRoleType)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name} - {role.description}
                      </option>
                    ))}
                  </select>
                <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple roles</p>
              </div>

              {selectedRoleType && (
                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                    {selectedRoleType === 'staff' ? 'Staff Organization' : 'Client Organization'}
                  </label>
                  <select
                    id="organization"
                    value={selectedRoleType === 'staff' ? createForm.staffOrganizationId : createForm.clientOrganizationId}
                    onChange={(e) => {
                      if (selectedRoleType === 'staff') {
                        setCreateForm({ ...createForm, staffOrganizationId: e.target.value })
                      } else {
                        setCreateForm({ ...createForm, clientOrganizationId: e.target.value })
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select organization</option>
                    {selectedRoleType === 'staff' && organizations.filter(org => org.type === 'staff').map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                    {selectedRoleType === 'client' && organizations.filter(org => org.type === 'client').map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={createForm.isActive}
                  onChange={(e) => setCreateForm({ ...createForm, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                  User is active
                </label>
              </div>

              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={!createForm.firstName || !createForm.lastName || !createForm.email || !createForm.password || createForm.roleIds.length === 0}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors"
                >
                  Create User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Edit User</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="editFirstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="editFirstName"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="editLastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="editLastName"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="editEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="editEmail"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                                  <label htmlFor="editRoles" className="block text-sm font-medium text-gray-700 mb-2">
                    Roles *
                  </label>
                  <select
                    multiple
                    id="editRoles"
                    value={editForm.roleIds}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value)
                      setEditForm({ ...editForm, roleIds: selectedOptions })
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name} - {role.description}
                      </option>
                    ))}
                  </select>
                <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple roles</p>
              </div>

              {/* Dynamic organization field based on user roles */}
              {editForm.roleIds.some(roleId => {
                const role = roles.find(r => r.id === roleId)
                return role?.name === 'client'
              }) ? (
                <div>
                  <label htmlFor="editClientOrganization" className="block text-sm font-medium text-gray-700 mb-2">
                    Client Organization
                  </label>
                  <select
                    id="editClientOrganization"
                    value={editForm.clientOrganizationId}
                    onChange={(e) => setEditForm({ ...editForm, clientOrganizationId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No client organization</option>
                    {organizations.filter(org => org.type === 'client').map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label htmlFor="editStaffOrganization" className="block text-sm font-medium text-gray-700 mb-2">
                    Staff Organization
                  </label>
                  <select
                    id="editStaffOrganization"
                    value={editForm.staffOrganizationId}
                    onChange={(e) => setEditForm({ ...editForm, staffOrganizationId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No staff organization</option>
                    {organizations.filter(org => org.type === 'staff').map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="editIsActive"
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="editIsActive" className="ml-2 block text-sm text-gray-900">
                  User is active
                </label>
              </div>

              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditUser}
                  disabled={!editForm.firstName || !editForm.lastName || !editForm.email || editForm.roleIds.length === 0}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors"
                >
                  Update User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Client Organization Modal */}
      {showAssignClientModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Assign User to Client Organization</h2>
              <button
                onClick={() => setShowAssignClientModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <p><strong>User:</strong> {selectedUser.firstName} {selectedUser.lastName}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
              </div>

              <div>
                <label htmlFor="clientOrganization" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Client Organization
                </label>
                <select
                  id="clientOrganization"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAssignClient(e.target.value)
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Choose a client organization...</option>
                  {organizations.filter(org => org.type === 'client').map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-xs text-gray-500">
                <p>This will assign the user to the selected client organization, allowing them to access client-specific features.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Reset Password</h2>
              <button
                onClick={() => setShowResetPasswordModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <p><strong>User:</strong> {selectedUser.firstName} {selectedUser.lastName}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={resetPasswordForm.newPassword}
                  onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, newPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter new password"
                  required
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={resetPasswordForm.confirmPassword}
                  onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <div className="text-xs text-gray-500">
                <p>Password must be at least 6 characters long. The user will need to use this new password to log in.</p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowResetPasswordModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={isResettingPassword}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md disabled:opacity-50"
                >
                  {isResettingPassword ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
